<template>
  <v-app-bar class="navigation-bar" color="primary" flat>
    <div class="navigation-bar__content">
      <v-btn
        class="navigation-bar__mobile-toggle"
        icon="mdi-menu"
        variant="text"
        aria-label="Open navigation menu"
        data-test-id="mobileNavigationToggle"
        @click="mobileMenuOpen = true"
      />

      <v-btn
        class="navigation-bar__brand"
        variant="text"
        :to="{ path: '/tandc' }"
        aria-label="MallShopLink home"
      >
        <img :src="logoSrc" alt="MallShopLink logo" />
      </v-btn>

      <div class="navigation-bar__links">
        <v-btn
          v-for="item in visibleNavItems"
          :key="item.labelKey"
          class="navigation-bar__button"
          :class="{ 'navigation-bar__button--active': isActive(item) }"
          variant="text"
          :to="item.to"
          :data-test-id="item.testId"
        >
          {{ $t(item.labelKey) }}
        </v-btn>
      </div>

      <v-spacer />

      <div class="navigation-bar__actions">
        <localeSwitch />
        <span v-if="user?.name" class="navigation-bar__user">{{ user.name }}</span>
        <v-btn
          v-if="!user"
          class="loginButton navigation-bar__login-button"
          variant="flat"
          data-test-id="loginButton"
          @click="login"
        >
          {{ $t('SignIn') }}
        </v-btn>
        <v-btn
          v-if="user"
          class="logoutButton"
          variant="text"
          data-test-id="logoutButton"
          :aria-label="$t('LogOut')"
          @click="logout"
        >
          <v-icon class="navigation-bar__logout-icon" icon="mdi-logout" size="20" />
          <span class="navigation-bar__logout-label">{{ $t('LogOut') }}</span>
        </v-btn>
      </div>
    </div>
  </v-app-bar>

  <v-navigation-drawer
    v-model="mobileMenuOpen"
    class="navigation-bar__mobile-panel"
    temporary
    location="start"
    data-test-id="mobileNavigationPanel"
  >
    <div class="navigation-bar__mobile-header">
      <img :src="logoSrc" alt="MallShopLink logo" />
      <v-btn
        icon="mdi-close"
        variant="text"
        aria-label="Close navigation menu"
        data-test-id="mobileNavigationClose"
        @click="closeMobileMenu"
      />
    </div>

    <v-list nav density="compact" class="navigation-bar__mobile-list">
      <v-list-item
        v-for="item in visibleNavItems"
        :key="item.labelKey"
        class="navigation-bar__mobile-item"
        :class="{ 'navigation-bar__mobile-item--active': isActive(item) }"
        :to="item.to"
        :active="isActive(item)"
        @click="closeMobileMenu"
      >
        {{ $t(item.labelKey) }}
      </v-list-item>
    </v-list>
  </v-navigation-drawer>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import AuthEngine from '@/plugins/msal'
import { useAppStore } from '@/stores/app'
import * as Sentry from '@sentry/browser'
import localeSwitch from '@/components/localeSwitch.vue'

interface NavigationItem {
  labelKey: string
  to: {
    path?: string
    name?: string
  }
  activePath: string
  requiresAuth?: boolean
  testId?: string
}

defineOptions({
  name: 'NavigationBar',
})

const route = useRoute()
const appStore = useAppStore()
const mobileMenuOpen = ref(false)

const logoSrc = '/assets/logo_horizontal.png'

const navigationItems: NavigationItem[] = [
  {
    labelKey: 'ImportRevenues',
    to: { path: '/Shops' },
    activePath: '/Shops',
    requiresAuth: true,
  },
  {
    labelKey: 'Reports',
    to: { path: '/reporting' },
    activePath: '/reporting',
    requiresAuth: true,
    testId: 'reportingNavLink',
  },
  {
    labelKey: 'Shops',
    to: { path: '/Shops/Admin' },
    activePath: '/Shops/Admin',
    requiresAuth: true,
  },
  {
    labelKey: 'Members',
    to: { path: '/Members/Admin' },
    activePath: '/Members/Admin',
    requiresAuth: true,
  },
  {
    labelKey: 'NotificationGroups',
    to: { name: 'notificationGroupAdmin' },
    activePath: '/notificationGroup/admin',
    requiresAuth: true,
  },
  {
    labelKey: 'Sections',
    to: { name: 'SectionsAdministration' },
    activePath: '/sections/admin',
    requiresAuth: true,
  },
  {
    labelKey: 'Chain',
    to: { path: '/Chains/Admin' },
    activePath: '/Chains/Admin',
    requiresAuth: true,
  },
  {
    labelKey: 'ContactUs',
    to: { path: '/ContactUs' },
    activePath: '/ContactUs',
  },
]

const user = computed(() => appStore.getAuthenticatedUser)
const path = computed(() => route.path)

const visibleNavItems = computed(() => {
  return navigationItems.filter((item) => {
    return !item.requiresAuth || Boolean(user.value)
  })
})

const isActive = (item: NavigationItem) => {
  return path.value === item.activePath
}

const login = () => {
  AuthEngine.login()
}

const logout = () => {
  closeMobileMenu()
  AuthEngine.logout()
}

const closeMobileMenu = () => {
  mobileMenuOpen.value = false
}

watch(
  user,
  (newUser) => {
    if (newUser?.name) {
      Sentry.setUser({
        username: newUser.name,
      })
      return
    }

    Sentry.setUser(null)
  },
  { immediate: true }
)
</script>

<style lang="scss">
@use '@/assets/styles/_variables' as vars;

.navigation-bar {
  color: vars.$white;
}

.navigation-bar .v-toolbar__content {
  height: auto !important;
  min-height: 64px;
  padding: 0.5rem 1rem;
}

.navigation-bar__content {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.5rem 1rem;
  width: 100%;
  min-width: 0;
}

.navigation-bar__mobile-toggle {
  display: none;
  color: inherit;
}

.navigation-bar__brand {
  min-width: auto;
  padding: 0;
}

.navigation-bar__brand img {
  display: block;
  width: auto;
  height: 36px;
}

.navigation-bar__links,
.navigation-bar__actions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.25rem;
}

.navigation-bar__button,
.navigation-bar__brand,
.navigation-bar .logoutButton {
  color: inherit;
  text-transform: none;
}

.navigation-bar__button--active {
  background-color: vars.$white;
  color: vars.$defaultGray;
}

.navigation-bar__user {
  padding-inline: 0.5rem;
  font-weight: 600;
}

.navigation-bar__logout-icon {
  display: none;
}

.navigation-bar .loginButton {
  text-transform: none;
}

.navigation-bar__login-button {
  background-color: vars.$white;
  color: vars.$darkBlue;
}

.navigation-bar__login-button:hover {
  background-color: vars.$light;
}

.navigation-bar .logoutButton:hover,
.navigation-bar__button:hover {
  color: vars.$defaultGray;
  background-color: vars.$light;
}

.navigation-bar__mobile-panel {
  color: vars.$defaultGray;
}

.navigation-bar__mobile-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 1rem;
  border-bottom: 1px solid rgba(15, 41, 60, 0.12);
}

.navigation-bar__mobile-header img {
  display: block;
  width: min(12rem, 68vw);
  height: auto;
}

.navigation-bar__mobile-list {
  padding: 0.5rem;
}

.navigation-bar__mobile-item {
  min-height: 44px;
  border-radius: 6px;
}

.navigation-bar__mobile-item--active {
  background-color: rgba(15, 41, 60, 0.08);
  color: vars.$darkBlue;
  font-weight: 700;
}

@media screen and (max-width: 1260px) {
  .navigation-bar__content {
    justify-content: center;
  }

  .navigation-bar__links,
  .navigation-bar__actions {
    width: 100%;
    justify-content: center;
  }

  .navigation-bar .v-spacer {
    display: none;
  }
}

@media screen and (max-width: 760px) {
  .navigation-bar .v-toolbar__content {
    min-height: 56px;
    padding: 0.5rem 0.75rem;
  }

  .navigation-bar__content {
    flex-wrap: nowrap;
    justify-content: space-between;
    gap: 0.5rem;
    overflow: hidden;
  }

  .navigation-bar__mobile-toggle {
    display: inline-flex;
    flex: 0 0 auto;
    min-width: 44px;
    width: 44px;
    height: 44px;
  }

  .navigation-bar__brand {
    flex: 1 1 auto;
    min-width: 0;
    justify-content: center;
    overflow: hidden;
  }

  .navigation-bar__brand img {
    max-width: min(10rem, 38vw);
    height: auto;
    max-height: 34px;
  }

  .navigation-bar__links {
    display: none;
  }

  .navigation-bar__actions {
    flex: 0 0 auto;
    width: auto;
    justify-content: flex-end;
    gap: 0.25rem;
  }

  .navigation-bar__actions .locale-switch__activator,
  .navigation-bar .logoutButton {
    width: 44px;
    min-width: 44px;
    height: 44px;
    padding: 0;
  }

  .navigation-bar__user {
    display: none;
  }

  .navigation-bar__logout-icon {
    display: inline-flex;
  }

  .navigation-bar__logout-label {
    display: none;
  }

  .navigation-bar .v-spacer {
    display: none;
  }

}

@media screen and (min-width: 761px) {
  .navigation-bar__mobile-panel {
    display: none;
  }
}
</style>
